import React, { useState } from 'react';
import { Users, Loader2, AlertCircle, RefreshCw } from '../icons';
import { GenderMale, Globe, type Icon } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { campaignKeys, useAudienceReport } from '@discovr/features';
import { useAiReviewSSE } from '@discovr/common';
import { Donut } from '../campaign-detail/components/insights/charts/Donut';
import { Button } from '../components/ui/button';
import { CountryFlag } from '../components/common/CountryFlag';
import { TruncatedText } from '../components/common/TruncatedText';
import { EmptyState } from '../components/common/EmptyState';
import { barScale, toDonutSegments, topSignalRows } from './audienceReportView';
import { isReportLoadFailed, isReportRefreshFailed } from './reportLoadState';
import { ReportRefreshNotice } from './ReportRefreshNotice';
import type { DonutSegment } from './audienceReportView';

// ── Chart colour palettes (raw hex — data-viz, exempt from token rules) ────────
// Donut + bar colours sampled from Figma "Audience Dynamics" (1672:33731).

const NEUTRAL = '#A3A3A3';

// Global Signal Mix bars (Figma 1672:33731): the #1 row is saturated, the rest
// tinted. Countries use the brand violet family, Languages a teal family.
const COUNTRY_TOP = '#6C09F7';
const COUNTRY_REST = '#B89CF5';
const LANG_TOP = '#1BBFA6';
const LANG_REST = '#7FD4CB';

const DONUT_GRID = {
  pair:   'grid grid-cols-1 gap-5 lg:grid-cols-2',
  single: 'grid grid-cols-1 gap-5',
} as const;

// Slice precision: gender in whole percents, age to one decimal.
const GENDER_DIGITS = 0;
const AGE_DIGITS = 1;

const LAST_COMPUTED_FORMAT: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };

const AGE_COLORS: Record<string, string> = {
  '13-17': '#FB923C',
  '18-24': '#34B4F4',
  '25-34': '#C063FD',
  '35-44': '#9647F8',
  '45-54': '#6C09F7',
  '55-64': '#F59E0B',
  '65+':   '#FB923C',
};

const GENDER_COLORS: Record<string, string> = {
  MALE: '#6C09F7', male: '#6C09F7',
  FEMALE: '#DA58FE', female: '#DA58FE',
  unknown: NEUTRAL,
};

// ── Props ─────────────────────────────────────────────────────────────────────

interface AudienceDynamicsTabProps {
  campaignId?: string;
}

// ── Formatters ────────────────────────────────────────────────────────────────

// Trim trailing zeros: 94.30 → "94.3", 95 → "95", 0.50 → "0.5".
const fmtVal = (n: number): string => {
  const r = Math.round(n * 100) / 100;
  return Number.isInteger(r) ? String(r) : String(parseFloat(r.toFixed(2)));
};

// ── Shared primitives ─────────────────────────────────────────────────────────

const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={`rounded-xl border border-border bg-card ${className ?? 'p-6'}`}>{children}</div>
);

// Figma (nodes 2454:42050, 2454:42075) specs this title at 18px/500/26px
// line-height — matches --text-heading-h7 exactly (was hardcoded to 20px).
const CardHead: React.FC<{ title: string; sub?: string }> = ({ title, sub }) => (
  <div className="mb-4">
    <h3 className="text-heading-h7 text-foreground">{title}</h3>
    {sub && <p className="mt-1 text-body-sm-regular text-muted-foreground">{sub}</p>}
  </div>
);

// Figma donut (nodes 2454:42050, 2454:42075): centred ring with a segment's
// value in the middle — the dominant segment by default, or whichever
// segment is hovered (ring or legend row; both stay in sync, matching the
// Donut component's other callers e.g. AnalyticsTab's Content Types card).
const DonutCard: React.FC<{
  title: string;
  sub: string;
  data: DonutSegment[];
  centerSuffix?: string;
  /** Shown when `data` is empty. Omit for a card that is only drawn with data. */
  empty?: { icon: Icon; title: string; body: string };
}> = ({ title, sub, data, centerSuffix, empty }) => {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const topIdx = data.reduce((best, s, i) => (s.value > (data[best]?.value ?? -Infinity) ? i : best), 0);
  const center = data[hoveredIdx ?? topIdx];
  const segments = data.map((s) => ({ pct: s.value, color: s.color }));
  return (
    <Card className="p-[22px]">
      <CardHead title={title} sub={sub} />
      {data.length === 0 ? (
        empty && <EmptyState icon={empty.icon} title={empty.title} body={empty.body} />
      ) : (
        <div className="flex flex-col items-center gap-6">
          {/* Figma 2454:42051 — 250.625px donut (≈251px). thickness=29 matches
              ChannelAverageDonut (Figma node 1741:55609) — the same
              "standalone composition card" donut pattern used by BrandSafety's
              Channel Average, already built to spec there (verified: same
              250px, 36px/14px center text, 12px/radius-4 legend chips, 10px
              lead gap, 24px donut-to-legend gap — everything but thickness
              already matched before this fix). Hover/click behavior below is
              untouched — only size, center-text sizing/spacing, and legend
              spacing changed. */}
          <div className="relative size-[250px]">
            <Donut segments={segments} size={250} thickness={29} activeIndex={hoveredIdx} onHoverChange={setHoveredIdx} />
            {center && (
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-0.5">
                <span className="text-[36px] font-medium leading-[43px] text-foreground">{center.value}%</span>
                <span className="text-[14px] font-medium tracking-[0.57px] text-muted-foreground">
                  {center.name}{centerSuffix}
                </span>
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            {data.map((s, i) => (
              <div
                key={s.name}
                onMouseEnter={() => setHoveredIdx(i)}
                onMouseLeave={() => setHoveredIdx(null)}
                className={`flex cursor-pointer items-center gap-2.5 rounded-md px-1.5 py-1 transition-colors ${hoveredIdx === i ? 'bg-muted' : ''}`}
              >
                <span className="size-3 shrink-0 rounded-sm" style={{ backgroundColor: s.color }} />
                <span className="text-[16px] font-medium text-[#404040]">{s.name}</span>
                <span className="text-[16px] font-semibold text-foreground">{s.value}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};

// One Global-Signal-Mix row: label (+ optional flag) left, value right, a
// brand-violet progress bar beneath (track = stone-200).
const SignalRow: React.FC<{
  label: React.ReactNode;
  /** Plain-text label used for the hover tooltip + truncation measurement when
   *  `label` is JSX (e.g. a flag + country name). Falls back to `label` when it
   *  is itself a string. */
  labelText?: string;
  value: number;
  max: number;
  color: string;
}> = ({ label, labelText, value, max, color }) => {
  const measureKey = labelText ?? (typeof label === 'string' ? label : '');
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <TruncatedText
          text={measureKey}
          content={labelText ?? label}
          className="flex items-center gap-2 text-[12px] font-medium text-foreground"
        >
          {label}
        </TruncatedText>
        <span className="shrink-0 text-[12px] font-medium text-foreground tabular-nums">{fmtVal(value)}</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-stone-200">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
};

// ── States ────────────────────────────────────────────────────────────────────

const LoadingState = () => (
  <div className="flex min-h-[320px] flex-col items-center justify-center gap-3">
    <Loader2 size={32} className="animate-spin text-muted-foreground" />
    <p className="text-[13px] font-medium text-muted-foreground">Loading audience dynamics…</p>
  </div>
);

const PendingState = ({ onRefresh }: { onRefresh: () => void }) => (
  <div className="flex min-h-[400px] flex-col items-center justify-center rounded-xl border border-border bg-card p-10 text-center">
    <div className="mb-4 flex size-12 items-center justify-center rounded-full bg-stone-100">
      <Users size={26} className="text-muted-foreground" />
    </div>
    <h3 className="mb-2 text-[18px] font-medium text-foreground">Audience Dynamics initialising</h3>
    <p className="mb-5 max-w-[340px] text-[13px] text-muted-foreground">
      The AI is analysing your campaign&apos;s audience. This usually takes a few minutes after the
      first video goes live. The page will update automatically.
    </p>
    <Button variant="outline" size="sm" onClick={onRefresh} className="gap-1.5">
      <RefreshCw size={12} className="size-3" /> Check again
    </Button>
  </div>
);

const ErrorState = ({ onRefresh }: { onRefresh: () => void }) => (
  <div className="flex min-h-[320px] flex-col items-center justify-center gap-3">
    <AlertCircle size={28} className="text-muted-foreground" />
    <p className="text-[13px] font-medium text-muted-foreground">Could not load audience dynamics.</p>
    <Button variant="outline" size="sm" onClick={onRefresh} className="gap-1.5">
      <RefreshCw size={12} className="size-3" /> Retry
    </Button>
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────

export function AudienceDynamicsTab({ campaignId }: AudienceDynamicsTabProps) {
  const queryClient = useQueryClient();
  const query = useAudienceReport(campaignId ?? '');
  const { data: report, isLoading, refetch } = query;

  // SSE: auto-refresh when AI signals campaign dynamics are ready
  useAiReviewSSE<{ campaign_id?: string }>({
    eventName: 'ai_audience_dynamics_campaign_ready',
    enabled:   Boolean(campaignId),
    filter:    (p) => p.campaign_id === campaignId,
    onReady:   () => {
      if (!campaignId) return;
      void queryClient.invalidateQueries({ queryKey: campaignKeys.audienceReport(campaignId) });
    },
  });

  const handleRefresh = () => { void refetch({ cancelRefetch: false }); };

  if (isLoading) return <LoadingState />;
  if (isReportLoadFailed(query) || report?.status === 'unavailable') return <ErrorState onRefresh={handleRefresh} />;
  if (!report || report.status === 'no_data') return <PendingState onRefresh={handleRefresh} />;

  // The backend has already picked the view-weighted split, sorted, dropped
  // zero buckets and named every row; this only turns fractions into percents.
  const genderData = toDonutSegments(report.gender, GENDER_COLORS, NEUTRAL, GENDER_DIGITS);
  const ageData = toDonutSegments(report.age, AGE_COLORS, NEUTRAL, AGE_DIGITS);

  const countryRows = topSignalRows(report.countries);
  const cityRows = topSignalRows(report.cities);
  const languageRows = topSignalRows(report.languages);
  const countryMax = barScale(countryRows);
  const cityMax = barScale(cityRows);
  const languageMax = barScale(languageRows);

  const hasSignalMix = countryRows.length > 0 || cityRows.length > 0 || languageRows.length > 0;
  // Top Cities (Figma 1736:59490 middle column) shows only when there are named city rows.
  const showCities = cityRows.length > 0;
  // X reports no viewer age, so the backend sends an empty split for it. An age
  // card that promised a breakdown "once views land" would be untrue, so it is
  // dropped and Gender takes the full row rather than sitting beside a gap.
  const showAgeCard = ageData.length > 0;
  const donutGrid = showAgeCard ? DONUT_GRID.pair : DONUT_GRID.single;

  return (
    <div className="flex flex-col gap-5">
      {isReportRefreshFailed(query) && <ReportRefreshNotice onRetry={handleRefresh} />}
      {/* ── Donuts: Generational + Gender ──────────────────────────────────── */}
      <div className={donutGrid}>
        {showAgeCard && (
          <DonutCard
            title="Generational Composition"
            sub="View-weighted audience demographics"
            data={ageData}
            centerSuffix=" Years"
          />
        )}
        <DonutCard
          title="Gender Composition"
          sub="View-weighted audience gender split"
          data={genderData}
          empty={{
            icon: GenderMale,
            title: 'No gender split yet',
            body: 'Gender split populates as your content earns views.',
          }}
        />
      </div>

      {/* ── Global Signal Mix ──────────────────────────────────────────────── */}
      <Card className="p-5">
        <CardHead title="Global Signal Mix" sub="View-weighted geography" />
        {!hasSignalMix ? (
          <EmptyState
            icon={Globe}
            title="No location map yet"
            body="Once content goes live, performance streams in within hours."
          />
        ) : (
          <div className={`grid grid-cols-1 gap-x-10 gap-y-6 ${showCities ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
            {countryRows.length > 0 && (
              <div className="flex flex-col gap-3">
                <div className="text-[12px] font-medium uppercase text-muted-foreground">Top Countries</div>
                {countryRows.map((r, i) => (
                  <SignalRow
                    key={r.code}
                    label={<><CountryFlag code={r.code} /> {r.name}</>}
                    labelText={r.name}
                    value={r.value}
                    max={countryMax}
                    color={i === 0 ? COUNTRY_TOP : COUNTRY_REST}
                  />
                ))}
              </div>
            )}
            {/* Top Cities — Figma 1736:59490 middle column: violet bars like
                countries, no flag. */}
            {showCities && (
              <div className="flex flex-col gap-3">
                <div className="text-[12px] font-medium uppercase text-muted-foreground">Top Cities</div>
                {cityRows.map((r, i) => (
                  <SignalRow
                    key={r.name}
                    label={r.name}
                    value={r.value}
                    max={cityMax}
                    color={i === 0 ? COUNTRY_TOP : COUNTRY_REST}
                  />
                ))}
              </div>
            )}
            {languageRows.length > 0 && (
              <div className="flex flex-col gap-3">
                <div className="text-[12px] font-medium uppercase text-muted-foreground">Languages</div>
                {languageRows.map((r, i) => (
                  <SignalRow
                    key={r.code}
                    label={r.name}
                    value={r.value}
                    max={languageMax}
                    color={i === 0 ? LANG_TOP : LANG_REST}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Footer */}
      {report.computed_at && (
        <p className="text-right text-[11px] text-muted-foreground">
          Last computed {new Date(report.computed_at).toLocaleString('en-US', LAST_COMPUTED_FORMAT)}
        </p>
      )}
    </div>
  );
}
